import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';
import type { DocumentType } from '../../types/tuition-assistance.ts';
import {
  ANALYSIS_TIMEOUT_MS,
  analysisErrorTags,
  analysisTimeoutTags,
  evaluateAiValidation,
  extractAiResult,
  type AiValidationContext,
  type ServiceResponsePayload,
} from './ai-validation.ts';

/** Structural slice of the cable service so tests can pass a stub. */
interface CableLike {
  subscribe(channelParams: object, callbacks: { received: (data: unknown) => void }): unknown;
  unsubscribe(channelParams: object): void;
}

export interface AiAnalysisWatcherOptions {
  cable: CableLike;
  /**
   * Build the evaluation context for a given declared type + row key. The
   * asset is provided so callers can scope expectations (e.g. course names)
   * to the asset's own attachable rather than an aggregate.
   */
  contextFor: (
    declaredType: DocumentType,
    uploadRowKey: string,
    asset: TasAssetModel
  ) => AiValidationContext;
  /** Persist the asset's mutated tags (typically `saveRecord(store, asset)`). */
  persist: (asset: TasAssetModel) => Promise<unknown>;
  /** Reload the asset with its asset-summary sideloaded (timeout recovery). */
  reload: (asset: TasAssetModel) => Promise<unknown>;
  /** Called after a verdict (or error/timeout fallback) is persisted. */
  onVerdict?: () => void;
  /** Override the broadcast wait window (tests). */
  timeoutMs?: number;
}

/**
 * Watches the AI agent's one-shot ServiceResponseChannel broadcast for
 * just-uploaded assets and persists the shared-evaluator verdict onto
 * `asset.tags` — the same client-side resolution the employee categorized
 * upload performs. Used by the admin approval drawers, where uploads would
 * otherwise stay `pending` forever ("Not Validated" to approvers).
 */
export class AiAnalysisWatcher {
  #options: AiAnalysisWatcherOptions;
  #timeouts = new Map<string, ReturnType<typeof setTimeout>>();
  #watchedUuids = new Set<string>();
  /**
   * Per-asset creation gates. Watches start BEFORE the create request (so no
   * broadcast is missed), but persisting a verdict on a still-`isNew` asset
   * would issue a second create — so verdict persistence buffers here until
   * `markCreated` (create settled) or resolves to a no-op on `unwatch`.
   */
  #creationGates = new Map<string, { promise: Promise<boolean>; resolve: (ok: boolean) => void }>();

  constructor(options: AiAnalysisWatcherOptions) {
    this.#options = options;
  }

  /** Subscribe to the AI broadcast for an asset and arm the timeout fallback. */
  watch(asset: TasAssetModel, uploadRowKey: string, declaredType: DocumentType) {
    const uuid = asset.uuid;
    this.#watchedUuids.add(uuid);
    let resolveGate!: (ok: boolean) => void;
    const gatePromise = new Promise<boolean>((resolve) => (resolveGate = resolve));
    this.#creationGates.set(uuid, { promise: gatePromise, resolve: resolveGate });
    this.#options.cable.subscribe(
      { channel: 'ServiceResponseChannel', request_id: uuid },
      {
        received: (data: unknown) => {
          void this.#handleResponse(
            asset,
            uploadRowKey,
            declaredType,
            data as ServiceResponsePayload
          );
        },
      }
    );
    const timeoutId = setTimeout(() => {
      void this.#handleTimeout(asset, uploadRowKey, declaredType);
    }, this.#options.timeoutMs ?? ANALYSIS_TIMEOUT_MS);
    this.#timeouts.set(uuid, timeoutId);
  }

  /**
   * Evaluate already-persisted summaries for docs stuck at `pending` — heals
   * documents whose uploading surface closed before the broadcast landed.
   * Assets whose summary is not sideloaded (route includes vary by surface)
   * are reloaded before deciding they have nothing to recover from.
   */
  async recoverPending(assets: TasAssetModel[]) {
    for (const asset of assets) {
      if (asset.attachmentDeletedAt !== null) continue;
      if (asset.aiValidationStatus !== 'pending') continue;
      const declaredType = asset.employeeSelectedType;
      if (!declaredType || declaredType === 'general') continue;
      let aiResult = extractAiResult(asset.assetSummary?.summary);
      if (!aiResult) {
        try {
          await this.#options.reload(asset);
        } catch (e) {
          console.warn('Failed to reload asset for recovered AI validation', e);
          continue;
        }
        // Another surface may have resolved the document while the reload was
        // in flight — never overwrite a terminal verdict.
        if (asset.aiValidationStatus !== 'pending') continue;
        aiResult = extractAiResult(asset.assetSummary?.summary);
        if (!aiResult) continue;
      }
      const outcome = evaluateAiValidation(
        aiResult,
        this.#options.contextFor(declaredType, asset.uploadRowKey ?? declaredType, asset)
      );
      await this.#applyTags(asset, outcome.tags);
    }
  }

  /** Signal that the watched asset's create request settled successfully. */
  markCreated(assetUuid: string) {
    const gate = this.#creationGates.get(assetUuid);
    if (gate) {
      gate.resolve(true);
      // Drop the resolved gate — late handlers proceed gateless because
      // creation has settled; keeping it would leak one entry per upload.
      this.#creationGates.delete(assetUuid);
    }
  }

  /** Cancel one asset's watch — e.g. when its create request failed. */
  unwatch(assetUuid: string) {
    this.#creationGates.get(assetUuid)?.resolve(false);
    this.#creationGates.delete(assetUuid);
    this.#clearWatch(assetUuid);
  }

  /** Clear every pending timer and subscription (call from willDestroy). */
  teardown() {
    for (const timeoutId of this.#timeouts.values()) {
      clearTimeout(timeoutId);
    }
    this.#timeouts.clear();
    for (const uuid of this.#watchedUuids) {
      this.#options.cable.unsubscribe({ channel: 'ServiceResponseChannel', request_id: uuid });
    }
    this.#watchedUuids.clear();
    for (const gate of this.#creationGates.values()) {
      gate.resolve(false);
    }
    this.#creationGates.clear();
  }

  /** True once the asset's create settled; false if the watch was cancelled. */
  async #awaitCreation(assetUuid: string) {
    const gate = this.#creationGates.get(assetUuid);
    // No gate means the asset was never watched (recovery path) — proceed.
    return gate ? gate.promise : true;
  }

  #clearWatch(uuid: string) {
    const timeoutId = this.#timeouts.get(uuid);
    if (timeoutId) clearTimeout(timeoutId);
    this.#timeouts.delete(uuid);
    if (this.#watchedUuids.has(uuid)) {
      this.#options.cable.unsubscribe({ channel: 'ServiceResponseChannel', request_id: uuid });
      this.#watchedUuids.delete(uuid);
    }
  }

  async #handleResponse(
    asset: TasAssetModel,
    uploadRowKey: string,
    declaredType: DocumentType,
    data: ServiceResponsePayload
  ) {
    this.#clearWatch(asset.uuid);
    if (!(await this.#awaitCreation(asset.uuid))) return;

    const context = this.#options.contextFor(declaredType, uploadRowKey, asset);
    const aiResult = extractAiResult(data?.data?.summary);
    const tags = aiResult
      ? evaluateAiValidation(aiResult, context).tags
      : analysisErrorTags(
          asset.tags,
          asset.employeeSelectedType ?? declaredType,
          context.translate
        );
    if (await this.#applyTags(asset, tags)) {
      this.#options.onVerdict?.();
    }
  }

  /** No broadcast within the window — try the persisted summary, else mark not_validated. */
  async #handleTimeout(asset: TasAssetModel, uploadRowKey: string, declaredType: DocumentType) {
    this.#clearWatch(asset.uuid);
    if (!(await this.#awaitCreation(asset.uuid))) return;
    if (asset.aiValidationStatus !== 'pending') return;

    try {
      await this.#options.reload(asset);
    } catch (e) {
      console.warn('Failed to reload asset for recovered AI validation', e);
    }
    // Another surface may have resolved the document while the reload was in
    // flight — never overwrite a terminal verdict.
    if (asset.aiValidationStatus !== 'pending') return;

    const aiResult = extractAiResult(asset.assetSummary?.summary);
    const tags = aiResult
      ? evaluateAiValidation(aiResult, this.#options.contextFor(declaredType, uploadRowKey, asset))
          .tags
      : analysisTimeoutTags(asset.tags, asset.employeeSelectedType ?? declaredType);
    if (await this.#applyTags(asset, tags)) {
      this.#options.onVerdict?.();
    }
  }

  /**
   * Persist mutated tags; on failure revert to the prior (pending) tags so a
   * later recovery pass can retry, and report whether the verdict stuck.
   * `onVerdict` must only fire when this returns true.
   */
  async #applyTags(asset: TasAssetModel, tags: Record<string, unknown>) {
    const previousTags = asset.tags;
    asset.tags = tags;
    try {
      await this.#options.persist(asset);
      return true;
    } catch (e) {
      console.warn('Failed to persist AI validation tags', e);
      asset.tags = previousTags;
      return false;
    }
  }
}
